import { useState, useEffect, useCallback } from "react";
import { apiGet } from "../lib/api.js";

/**
 * A single folder entry returned by the WorkDrive API.
 */
interface WorkDriveFolder {
	id: string;
	name: string;
	modifiedAt: string;
}

/**
 * API response shape for both root and child folder endpoints.
 */
interface FolderListResponse {
	folders: WorkDriveFolder[];
	hasMore?: boolean;
	nextCursor?: string;
}

/**
 * One entry in the navigation stack.
 * id = "root" at the top level.
 */
interface NavEntry {
	id: string;
	name: string;
}

export interface FolderExplorerProps {
	/** Zoho project ID — currently unused but reserved for project-scoped folder browsing. */
	projectId?: string;
	/** Called with the selected folder ID and name when the user picks a folder. */
	onSelect: (folderId: string, folderName: string) => void;
	/** Optional close handler — shown as a Cancel action when provided. */
	onClose?: () => void;
	/** Optional additional CSS class name. */
	className?: string;
}

/**
 * Formats a UTC date string as a human-readable relative time label.
 * Examples: "Just now", "2h ago", "Yesterday", "3d ago", "14 Feb"
 */
function formatRelativeTime(dateString: string): string {
	const date = new Date(dateString);
	if (isNaN(date.getTime())) return "Unknown";
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	if (diffMs < 0) return "Just now";
	const diffMins = Math.floor(diffMs / 60_000);
	const diffHours = Math.floor(diffMs / 3_600_000);
	const diffDays = Math.floor(diffMs / 86_400_000);

	if (diffMins < 2) return "Just now";
	if (diffMins < 60) return `${diffMins}m ago`;
	if (diffHours < 24) return `${diffHours}h ago`;
	if (diffDays === 1) return "Yesterday";
	if (diffDays < 7) return `${diffDays}d ago`;

	return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/**
 * Reusable folder browser component for navigating and selecting WorkDrive folders.
 * Extracted from FolderExplorerModal so it can be embedded inline (e.g. in the setup wizard)
 * as well as in the modal overlay.
 *
 * Navigation model:
 *  - Back / Forward buttons traverse the navStack history.
 *  - Double-clicking a folder pushes it onto the stack and navigates into it.
 *  - Breadcrumb segments are clickable shortcuts to any ancestor level.
 *  - Single-click selects; the parent calls onSelect with (folderId, folderName).
 */
export default function FolderExplorer({
	onSelect,
	onClose,
	className,
}: FolderExplorerProps) {
	/* ------------------------------------------------------------------ */
	/*  Navigation stack                                                    */
	/* ------------------------------------------------------------------ */
	const [navStack, setNavStack] = useState<NavEntry[]>([
		{ id: "root", name: "Client Projects" },
	]);
	const [navIndex, setNavIndex] = useState(0);

	/* ------------------------------------------------------------------ */
	/*  Folder list state                                                   */
	/* ------------------------------------------------------------------ */
	const [folders, setFolders] = useState<WorkDriveFolder[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [hasMore, setHasMore] = useState(false);
	const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);

	/* ------------------------------------------------------------------ */
	/*  Selection state                                                     */
	/* ------------------------------------------------------------------ */
	const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

	/* ------------------------------------------------------------------ */
	/*  Derived values                                                      */
	/* ------------------------------------------------------------------ */
	const currentEntry = navStack[navIndex];
	const canGoBack = navIndex > 0;
	const canGoForward = navIndex < navStack.length - 1;

	/* ------------------------------------------------------------------ */
	/*  Data fetching                                                       */
	/* ------------------------------------------------------------------ */
	const fetchFolder = useCallback(
		async (entry: NavEntry, cursor?: string): Promise<void> => {
			if (!cursor) {
				setLoading(true);
				setFolders([]);
				setError(null);
				setHasMore(false);
				setNextCursor(undefined);
			} else {
				setLoadingMore(true);
			}

			try {
				const url =
					entry.id === "root"
						? "/api/workdrive/root-folders"
						: `/api/workdrive/folders/${entry.id}${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`;

				const response = await apiGet(url);

				if (!response.ok) {
					const errorData = await response.json().catch(() => null) as
						| { message?: string }
						| null;
					setError(
						errorData?.message ??
							`Failed to load folders (${response.status})`,
					);
					return;
				}

				const data = (await response.json()) as FolderListResponse;

				if (cursor) {
					setFolders((prev) => [...prev, ...data.folders]);
				} else {
					setFolders(data.folders);
				}

				setHasMore(data.hasMore ?? false);
				setNextCursor(data.nextCursor);
			} catch {
				setError("Failed to connect to the server. Please try again.");
			} finally {
				setLoading(false);
				setLoadingMore(false);
			}
		},
		[],
	);

	/* Fetch whenever the current nav entry changes */
	useEffect(() => {
		setSelectedFolderId(null);
		void fetchFolder(currentEntry);
	}, [currentEntry, fetchFolder]);

	/* ------------------------------------------------------------------ */
	/*  Navigation helpers                                                  */
	/* ------------------------------------------------------------------ */
	function navigateInto(folder: WorkDriveFolder): void {
		const newEntry: NavEntry = { id: folder.id, name: folder.name };
		const newStack = [...navStack.slice(0, navIndex + 1), newEntry];
		setNavStack(newStack);
		setNavIndex(newStack.length - 1);
	}

	function goBack(): void {
		if (canGoBack) setNavIndex((i) => i - 1);
	}

	function goForward(): void {
		if (canGoForward) setNavIndex((i) => i + 1);
	}

	function goToBreadcrumb(index: number): void {
		setNavIndex(index);
	}

	function refresh(): void {
		void fetchFolder(currentEntry);
	}

	function loadMore(): void {
		if (nextCursor && currentEntry.id !== "root") {
			void fetchFolder(currentEntry, nextCursor);
		}
	}

	/* ------------------------------------------------------------------ */
	/*  Row interaction handlers                                            */
	/* ------------------------------------------------------------------ */
	function handleRowClick(folder: WorkDriveFolder): void {
		setSelectedFolderId(folder.id);
		/* Notify parent of the selection immediately */
		onSelect(folder.id, folder.name);
	}

	function handleRowDoubleClick(folder: WorkDriveFolder): void {
		navigateInto(folder);
	}

	function handleRowKeyDown(
		event: React.KeyboardEvent,
		folder: WorkDriveFolder,
	): void {
		if (event.key === "Enter") {
			if (selectedFolderId === folder.id) {
				navigateInto(folder);
			} else {
				setSelectedFolderId(folder.id);
				onSelect(folder.id, folder.name);
			}
		}
		if (event.key === " ") {
			event.preventDefault();
			setSelectedFolderId(folder.id);
			onSelect(folder.id, folder.name);
		}
	}

	/* ------------------------------------------------------------------ */
	/*  Render                                                              */
	/* ------------------------------------------------------------------ */
	const folderCount = folders.length;
	const selectedName = folders.find((f) => f.id === selectedFolderId)?.name;

	return (
		<div className={`folder-explorer-content-wrapper${className ? ` ${className}` : ""}`}>
			{/* Toolbar — back / forward / refresh + breadcrumb */}
			<div className="folder-explorer-toolbar">
				<div className="folder-explorer-nav-btns">
					<button
						type="button"
						className="folder-explorer-nav-btn"
						onClick={goBack}
						disabled={!canGoBack}
						aria-label="Go back"
					>
						&#9664;
					</button>
					<button
						type="button"
						className="folder-explorer-nav-btn"
						onClick={goForward}
						disabled={!canGoForward}
						aria-label="Go forward"
					>
						&#9654;
					</button>
					<button
						type="button"
						className="folder-explorer-nav-btn"
						onClick={refresh}
						aria-label="Refresh folder contents"
					>
						&#8635;
					</button>
				</div>

				<nav
					className="folder-explorer-breadcrumb"
					aria-label="Folder navigation breadcrumb"
				>
					{navStack.slice(0, navIndex + 1).map((entry, index) => {
						const isLast = index === navIndex;
						return (
							<span key={`${entry.id}-${index}`} className="folder-explorer-breadcrumb-segment">
								{index > 0 && (
									<span
										className="folder-explorer-breadcrumb-sep"
										aria-hidden="true"
									>
										›
									</span>
								)}
								{isLast ? (
									<span className="folder-explorer-breadcrumb-current">
										{entry.name}
									</span>
								) : (
									<button
										type="button"
										className="folder-explorer-breadcrumb-link"
										onClick={() => goToBreadcrumb(index)}
									>
										{entry.name}
									</button>
								)}
							</span>
						);
					})}
				</nav>
			</div>

			{/* Content area — grid */}
			<div
				className="folder-explorer-content"
				role="grid"
				aria-label="Folder list"
			>
				<div className="folder-explorer-col-headers" role="row">
					<span role="columnheader" className="folder-explorer-col-name">
						Name
					</span>
					<span role="columnheader" className="folder-explorer-col-modified">
						Modified
					</span>
				</div>

				{loading ? (
					<div className="folder-explorer-state-msg">
						<div className="loading-spinner folder-explorer-spinner" />
						<span>Loading folders…</span>
					</div>
				) : error ? (
					<div className="folder-explorer-state-msg folder-explorer-error-msg">
						<p>{error}</p>
						<button
							type="button"
							className="folder-explorer-retry-btn"
							onClick={refresh}
						>
							Try again
						</button>
					</div>
				) : folders.length === 0 ? (
					<div className="folder-explorer-state-msg folder-explorer-empty-msg">
						This folder is empty
					</div>
				) : (
					<>
						{folders.map((folder) => {
							const isSelected = folder.id === selectedFolderId;
							return (
								<div
									key={folder.id}
									role="row"
									aria-selected={isSelected}
									className={`folder-explorer-row${isSelected ? " folder-explorer-row--selected" : ""}`}
									onClick={() => handleRowClick(folder)}
									onDoubleClick={() => handleRowDoubleClick(folder)}
									onKeyDown={(e) => handleRowKeyDown(e, folder)}
									tabIndex={0}
								>
									<span role="gridcell" className="folder-explorer-row-name">
										<span
											className="folder-explorer-row-icon"
											aria-hidden="true"
										>
											📁
										</span>
										{folder.name}
									</span>
									<span role="gridcell" className="folder-explorer-row-modified">
										{formatRelativeTime(folder.modifiedAt)}
									</span>
								</div>
							);
						})}

						{hasMore && (
							<div className="folder-explorer-load-more-row">
								<button
									type="button"
									className="folder-explorer-load-more-btn"
									onClick={loadMore}
									disabled={loadingMore}
									aria-label="Load more folders"
								>
									{loadingMore ? "Loading…" : "Load more"}
								</button>
							</div>
						)}
					</>
				)}
			</div>

			{/* Status bar */}
			<div className="folder-explorer-statusbar">
				<span className="folder-explorer-status-count">
					{loading
						? "Loading…"
						: `${folderCount} item${folderCount !== 1 ? "s" : ""}${selectedFolderId ? " · 1 selected" : ""}`}
					{selectedName && (
						<span className="folder-explorer-status-selected">
							{" — "}{selectedName}
						</span>
					)}
				</span>
				{onClose && (
					<div className="folder-explorer-actions">
						<button
							type="button"
							className="folder-explorer-cancel-btn"
							onClick={onClose}
						>
							Cancel
						</button>
					</div>
				)}
			</div>
		</div>
	);
}
